<template>
  <div class="bxp-form">
    <span class="bxp-desktop-header">
      <h1>
        Please provide your <strong>contact details</strong> below.
      </h1>
    </span>
    <span class="bxp-mobile-header">
      <h1>
        Scale your business with our <strong>Enterprise plan</strong>.
      </h1>
      <p class="heading-sub-text">
        Powerful, flexible and customised to fit your needs today and support your growth tomorrow.
      </p>
    </span>
    <div class="bxp-form-fields">
      <v-text-field
        id="first_name"
        label="First name"
        prepend-inner-icon="fas fa-user"
        color="secondary"
        @change="updateFirstName($event)"
        :append-outer-icon="fields.firstName.validationIcon"
        :success="fields.firstName.valid"
        :error="fields.firstName.valid === false"
        :hint="fields.firstName.validationMessage"
        :persistent-hint="true"
        v-scroll-to="'#first_name'"
      />

      <v-text-field
        label="Last name"
        prepend-inner-icon="fas fa-user"
        color="secondary"
        @change="updateLastName($event)"
        :append-outer-icon="fields.lastName.validationIcon"
        :success="fields.lastName.valid"
        :error="fields.lastName.valid === false"
        :hint="fields.lastName.validationMessage"
        :persistent-hint="true"
      />

      <v-autocomplete
        id="country"
        label="Country"
        v-model="countryValue"
        :items="fields.country.options"
        item-text="name"
        item-value="code"
        prepend-inner-icon="fas fa-map-marker-alt"
        color="secondary"
        :success="fields.country.valid"
        v-scroll-to="{
          el: '#country',
          offset: -40
        }"
      >
        <template v-slot:append-outer>
          <v-tooltip left :open-on-hover="countryValue !== 'ZA'" max-width="320px">
            <template v-slot:activator="{ on, attrs }">
              <v-icon 
                v-bind="attrs" 
                v-on="countryValue !== 'ZA' ? on : null" 
                :color="countryValue !== 'ZA' ? 'accent' : 'primary'"
              >{{ fields.country.validationIcon }}</v-icon>
            </template>
            <span>{{ fields.country.validationMessage }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <vuetify-phone-field
        id="mobile_number"
        label="Mobile number"
        color="secondary"
        @updated="mobileNumberValue = $event.number"
        :region="countryValue"
        @valid="updateMobileNumberValidation($event)"
        :success="fields.mobileNumber.valid"
        :error="fields.mobileNumber.valid === false"
        :hint="fields.mobileNumber.validationMessage"
        prepend-inner-icon="fas fa-phone"
        :append-outer-icon="fields.mobileNumber.validationIcon"
        :persistent-hint="true"
        autocomplete="nope"
        v-scroll-to="'#mobile_number'"
      >
        <template v-slot:append>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-icon v-on="on" class="bxp-tooltip-inner">far fa-question-circle</v-icon>
            </template>
            We will not share your mobile number with third party suppliers nor use it to spam you.
          </v-tooltip>
        </template>
      </vuetify-phone-field>

      <v-text-field
        label="Work email address"
        color="secondary"
        prepend-inner-icon="fas fa-envelope"
        v-model="emailValue"
        :append-outer-icon="fields.email.validationIcon"
        :success="fields.email.valid"
        :error="fields.email.valid === false"
        :hint="fields.email.validationMessage"
        :persistent-hint="true"
        :loading="fields.email.loading"
        autocomplete="off"
      />

    </div>

  </div>
</template>

<script>
import axios from "axios";
import { VueMaskDirective } from "v-mask";
import { formBus } from "@/formBus";
export default {
  directives: {
    mask: VueMaskDirective
  },
  data() {
    return {
      error: false,
      loadingScreen: {
        show: false,
        success: false
      }
    };
  },
  computed: {
    fields() {
      return formBus.fields
    },
    stepValid() {
      return (
        this.fields.firstName.valid &&
        this.fields.lastName.valid &&
        this.fields.email.valid &&
        this.fields.country.valid &&
        this.fields.mobileNumber.valid
      )
    },
    emailValue: {
      get() {
        return this.fields.email.value;
      },
      set(val) {
        formBus.fields.email.loading = true;
        val = val.trim();
        formBus.fields.email.value = val;
        this.resetValidation("email", val);
        if (formBus.fields.email.timer !== null) {
          clearTimeout(formBus.fields.email.timer);
        }
        formBus.fields.email.timer = setTimeout(
          async val => {
            const emailValidation = await this.validateEmail(val);
            this.updateValidation(
              "email",
              emailValidation.valid,
              emailValidation.message
            );
            formBus.fields.email.loading = false;
          },
          2500,
          val
        );
      }
    },
    countryValue: {
      get() {
        return this.fields.country.value;
      },
      set(val) {
        formBus.fields.country.value = val;
        this.validateCountry(val);
      }
    },
    mobileNumberValue: {
      get() {
        return this.fields.mobileNumber.value;
      },
      set(val) {
        formBus.fields.mobileNumber.value = val;
      }
    }
  },
  methods: {
    updateFirstName(val) {
      this.fields.firstName.value = val;
      let valid = null;
      let message = null;
      if (val === "") {
        valid = false;
        message = "This field is incomplete";
      } else {
        valid = true;
        message = null;
      }
      this.updateValidation("firstName", valid, message);
    },
    updateLastName(val) {
      this.fields.lastName.value = val;
      let valid = null;
      let message = null;
      if (val === "") {
        valid = false;
        message = "This field is incomplete";
      } else {
        valid = true;
        message = null;
      }
      this.updateValidation("lastName", valid, message);
    },
    async validateEmail(email) {
      //Empty field
      if (email === "") {
        return {
          valid: false,
          message: "This field is incomplete"
        };
      }
      //Email format
      const validCriteria = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      if (!validCriteria.test(email)) {
        return {
          valid: false,
          message: "Please enter a valid email address"
        };
      }
      //Uses a domain that's not blacklisted as non-work emails
      if (!this.isWorkEmail(email)) {
        return {
          valid: false,
          message: "Please enter your work email address"
        };
      }
      return {
        valid: true,
        message: null
      };
    }
  }
};
</script>
